import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getPage, listPages, postPageOps, type PageInfo } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { useLiveRows, type LiveWire } from '../board/useLiveRows'
import type { PageProps, PageRow } from './pageModel'

/**
 * A ROOM'S PAGES — owner, 2026-11-02: 把 Notion 這個頁面編輯的功能照抄過來 … 之後我們可以有自己的頁面功能 (還能夠跟
 * Agents 共同編輯, 以及與朋友在 Project Chat 裡面協作).
 *
 * The pages are open when the URL says `?page=<conversation>`, one of them when `&pg=<page>` rides beside it — a refresh
 * lands back there, Back leaves, a link can be shared with the room (as boardNav does for whiteboards).
 */
/**
 * Whether this BUILD offers pages. A dev build says `VITE_PAGES=on`; a prod build does not until the service there has
 * pages (chat lambda/pages.ts) — prod's web is built from main, so a release of anything else must never show a button
 * whose every call 404s. Off unless the build says `on`, read at call time (as artifacts/publicFlag.ts).
 */
export function pagesOn(): boolean {
  const env = import.meta.env as Record<string, string | undefined>
  return env['VITE_PAGES'] === 'on'
}

const PARAM = 'page'
const WHICH = 'pg'
const EVENT = 'avg-page-nav'
const PAGE_ID = /^[a-z0-9]{4,24}$/
/** This device remembers the page last open in each room, so the Pages button goes back to it. */
const LAST = 'avg.page.last.'

export function openPagesConv(): string | null {
  const id = new URLSearchParams(window.location.search).get(PARAM)
  return id && /^[0-9a-f]{16}$/.test(id) ? id : null
}

export function openPageSel(): string | null {
  const p = new URLSearchParams(window.location.search).get(WHICH)
  return p && PAGE_ID.test(p) ? p : null
}

export function lastPage(conv: string): string | null {
  try {
    const p = localStorage.getItem(LAST + conv)
    return p && PAGE_ID.test(p) ? p : null
  } catch {
    return null
  }
}

export function rememberPage(conv: string, page: string | null): void {
  try {
    if (page) localStorage.setItem(LAST + conv, page)
    else localStorage.removeItem(LAST + conv)
  } catch {
    /* a private window: the list next time */
  }
}

function go(q: URLSearchParams, push: boolean): void {
  const s = q.toString()
  const url = `${window.location.pathname}${s ? `?${s}` : ''}${window.location.hash}`
  if (push) window.history.pushState(window.history.state, '', url)
  else window.history.replaceState(window.history.state, '', url)
  window.dispatchEvent(new Event(EVENT))
}

/** Open the room's pages — on `page`, else the one this device had open there last. */
export function openPages(conv: string, page: string | null = lastPage(conv)): void {
  const q = new URLSearchParams(window.location.search)
  const was = q.get(PARAM)
  q.set(PARAM, conv)
  if (page) q.set(WHICH, page)
  else q.delete(WHICH)
  if (page) rememberPage(conv, page)
  go(q, was !== conv)
}

/** Another of the room's pages, in place (null: the list) — Back still leaves the pages. */
export function selectPage(page: string | null): void {
  const q = new URLSearchParams(window.location.search)
  const conv = q.get(PARAM)
  if (!conv) return
  rememberPage(conv, page)
  if ((q.get(WHICH) ?? null) === page) return
  if (page) q.set(WHICH, page)
  else q.delete(WHICH)
  go(q, false)
}

export function closePages(): void {
  const q = new URLSearchParams(window.location.search)
  if (!q.has(PARAM)) return
  q.delete(PARAM)
  q.delete(WHICH)
  go(q, false)
}

/**
 * Which page an agent's session drawer opens on (owner, 2026-11-03: 在 Agent session 新增一個 page icon (如同 whiteboard
 * icon) … 直接從 Agent session 裡面瀏覽/打開 project chat 裡的 page) — as its whiteboard drawer does (board/agentBoards
 * agentBoard): of its rooms' pages, the one the agent wrote to last; else, in its first room, the page this device had
 * open there last (`last`), when the room still has it; else that room's list (null).
 */
export function agentPage(
  rooms: string[],
  lists: (PageInfo[] | undefined)[],
  hash: string,
  last: (conv: string) => string | null,
): { conv: string; page: string | null } | null {
  let best: { conv: string; page: string } | null = null
  let at = -1
  rooms.forEach((conv, i) => {
    for (const p of lists[i] ?? []) {
      if (p.last_via === hash && (p.last_ts ?? 0) > at) {
        best = { conv, page: p.page }
        at = p.last_ts ?? 0
      }
    }
  })
  if (best) return best
  const conv = rooms[0]
  if (!conv) return null
  const mine = last(conv)
  const known = lists[0]
  return { conv, page: mine && (!known || known.some((p) => p.page === mine)) ? mine : null }
}

const ROOM_PAGE = 'avg-room-page'

/**
 * A page a chat message names (its chip — chat/ProjectPanel), opened where the reader is: the room's pages drawer when
 * they are in that room, else the full screen (App listens; owner, 2026-11-02: 與朋友在 Project Chat 裡面協作).
 */
export function openRoomPage(conv: string, page: string): void {
  window.dispatchEvent(new CustomEvent(ROOM_PAGE, { detail: { conv, page } }))
}

export function onRoomPage(fn: (conv: string, page: string) => void): () => void {
  const on = (e: Event) => {
    const d = (e as CustomEvent<{ conv: string; page: string }>).detail
    if (d && typeof d.conv === 'string' && typeof d.page === 'string') fn(d.conv, d.page)
  }
  window.addEventListener(ROOM_PAGE, on)
  return () => window.removeEventListener(ROOM_PAGE, on)
}

/** Call `fn` whenever the open pages may have changed (opened, closed, another picked, Back/Forward). */
export function onPageNav(fn: () => void): () => void {
  window.addEventListener('popstate', fn)
  window.addEventListener(EVENT, fn)
  return () => {
    window.removeEventListener('popstate', fn)
    window.removeEventListener(EVENT, fn)
  }
}

// ── the room's list ────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Pages made, renamed or deleted, into the list: replaced where they were, else added, a deleted one taken out; in the
 * order made. How far this person has seen one stays: a frame is the room's, and never says that.
 */
export function mergePages(cur: PageInfo[], changed: Array<PageInfo | { page: string; del: true }>): PageInfo[] {
  const byId = new Map(cur.map((p) => [p.page, p]))
  for (const p of changed) {
    if ('del' in p) {
      byId.delete(p.page)
      continue
    }
    const seen = p.seen ?? byId.get(p.page)?.seen
    byId.set(p.page, seen === undefined ? p : { ...p, seen })
  }
  return [...byId.values()].sort((a, b) => a.created_ts - b.created_ts || (a.page < b.page ? -1 : 1))
}

export function usePages(convId: string) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['pages', convId], queryFn: async () => (await listPages(convId)).pages })
  useEffect(() => {
    const key = ['pages', convId]
    const offFrame = onChatv1Frame((f) => {
      if (f.type === 'pages' && f.conv_id === convId) qc.setQueryData<PageInfo[]>(key, (cur) => mergePages(cur ?? [], f.pages))
      if (f.type === 'page' && f.conv_id === convId) {
        qc.setQueryData<PageInfo[]>(key, (cur) =>
          cur?.map((p) => (p.page === f.page && f.rev > p.rev ? { ...p, rev: f.rev, last_by: f.by, ...(f.via ? { last_via: f.via } : {}) } : p)),
        )
      }
      if (f.type === 'page_seen' && f.conv_id === convId) {
        qc.setQueryData<PageInfo[]>(key, (cur) => cur?.map((p) => (p.page === f.page && f.rev > (p.seen ?? 0) ? { ...p, seen: f.rev } : p)))
      }
    })
    const offOpen = onChatv1Open(() => void qc.invalidateQueries({ queryKey: key }))
    return () => {
      offFrame()
      offOpen()
    }
  }, [convId, qc])
  /** Put an answer the service just gave into the list (the frame for it may come a moment later, or never). */
  const put = (p: PageInfo | { page: string; del: true }) => qc.setQueryData<PageInfo[]>(['pages', convId], (cur) => mergePages(cur ?? [], [p]))
  return { pages: q.data ?? null, failed: q.isError, put }
}

// ── one page, live ─────────────────────────────────────────────────────────────────────────────────────────────

/** What a refusal means to the person, in words. */
function refusalText(code: string): string {
  switch (code) {
    case 'locked':
      return 'This page is locked: only the people its owner chose can change it.'
    case 'not_a_member':
      return 'You are no longer in this project, so the page cannot be changed.'
    case 'deleted':
      return 'Someone deleted that block a moment ago.'
    case 'no_such_page':
      return 'That page is no longer in this project.'
    default:
      return 'Some of that could not be saved.'
  }
}

const PAGE_WIRE: LiveWire<PageRow, PageProps> = {
  read: async (conv, page) => {
    const r = await getPage(conv, page)
    return { rev: r.rev, rows: r.blocks }
  },
  post: async (conv, page, ops, client) => {
    const r = await postPageOps(conv, page, ops, client)
    return { rev: r.rev, rows: r.blocks, ...(r.refused ? { refused: r.refused } : {}) }
  },
  frame: (f, conv, page) =>
    f.type === 'page' && f.conv_id === conv && f.page === page
      ? {
          by: f.by,
          ...(f.via ? { via: f.via } : {}),
          ...(f.client ? { client: f.client } : {}),
          rev: f.rev,
          ...(f.blocks ? { rows: f.blocks } : {}),
          ...(f.reload ? { reload: true } : {}),
        }
      : null,
  missing: 'no_such_page',
  refusal: refusalText,
}

/** One page's blocks, live — the editor keeps its own undo (no `inverse`). */
export function usePage(convId: string | null, pageId: string) {
  return useLiveRows(PAGE_WIRE, convId, pageId)
}

